import { zodResolver } from '@hookform/resolvers/zod'
import { CreditCard, Loader2, Paperclip } from 'lucide-react'
import { useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { FormSelect } from '@/components/ui/select'
import { isApiError } from '@/lib/api/errors'
import { fmtDate, fmtMoney } from '../../shared/format'
import { useSavePayment, useTransactions } from '../api'
import { PAYMENT_CHANNELS, type Transaction } from '../types'
import { toastError, useFinanceLookups } from '../ui'
import { CheckRow, Field, FormSection, ReadonlyValue } from './FormBits'
import { SensitiveIban } from './SensitiveIban'

type Props = { open: boolean; transaction: Transaction | null; onOpenChange: (v: boolean) => void }

export function PaymentFormModal({ open, onOpenChange, ...rest }: Props) {
  return open ? <PaymentForm key={rest.transaction?.id ?? 'any'} {...rest} onDone={() => onOpenChange(false)} /> : null
}

function PaymentForm({ transaction: fixed, onDone }: Omit<Props, 'open' | 'onOpenChange'> & { onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSavePayment()
  const txs = useTransactions()
  const { accounts, partyName, party, accountDetail } = useFinanceLookups()
  const fileRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState('')
  const open = (txs.data ?? []).filter((x) => x.status !== 'paid')

  const schema = z.object({
    transactionId: z.string().min(1, t('finance.pay.form.txRequired')),
    partial: z.boolean(),
    amount: z.string().regex(/^\d+(\.\d{1,2})?$/, t('finance.form.amountInvalid')).refine((v) => Number(v) > 0, t('finance.form.amountInvalid')),
    date: z.string().min(1, t('finance.form.dateRequired')),
    accountId: z.string().min(1, t('finance.pay.form.accountRequired')),
    channel: z.enum(PAYMENT_CHANNELS),
    reference: z.string().trim().max(60),
    note: z.string().trim().max(240),
  })
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { transactionId: fixed?.id ?? '', partial: false, amount: fixed?.remaining ?? '', date: new Date().toISOString().slice(0, 10), accountId: '', channel: 'transfer' as const, reference: '', note: '' },
  })
  const { errors } = form.formState
  const [txId, partial] = useWatch({ control: form.control, name: ['transactionId', 'partial'] })
  const tx = fixed ?? open.find((x) => x.id === txId)

  const onSubmit = form.handleSubmit(async ({ partial: _p, ...v }) => {
    void _p
    try {
      const res = await save.mutateAsync({ ...v, hasReceipt: !!fileName })
      if (res.pendingApproval)
        toast.info(t('finance.paymentRequests.sent'), { description: t('finance.paymentRequests.sentHint') })
      else toast.success(t(tx?.kind === 'income' ? 'finance.pay.collected' : 'finance.pay.paid'))
      onDone()
    } catch (e) {
      if (isApiError(e) && e.code === 'PAYMENT_EXCEEDS_REMAINING') form.setError('amount', { message: t('errors.PAYMENT_EXCEEDS_REMAINING') })
      else toastError(e)
    }
  })

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<CreditCard />}
      title={t('finance.pay.form.title')}
      description={t('finance.pay.form.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <FormSection title={t('finance.pay.form.sectionMatch')}>
          <Field id="pm-tx" label={t('finance.pay.form.tx')} error={errors.transactionId?.message} full>
            <FormSelect
              id="pm-tx"
              control={form.control}
              name="transactionId"
              disabled={!!fixed}
              placeholder={t('common.select')}
              onValueChange={(id) => {
                const x = open.find((o) => o.id === id)
                if (x) form.setValue('amount', x.remaining)
                form.setValue('partial', false)
              }}
              options={(fixed ? [fixed] : open).map((x) => ({
                value: x.id,
                label: `${partyName(x.counterpartyId)} · ${x.docNo || x.description}`,
                description: `${t(`finance.kind.${x.kind}`)} · ${fmtDate(x.dueDate)} · ${fmtMoney(x.remaining)}`,
                keywords: [x.docNo, x.description, partyName(x.counterpartyId)],
              }))}
            />
          </Field>
          <Field id="pm-remaining" label={t('finance.tx.remaining')}>
            <ReadonlyValue id="pm-remaining">{tx ? fmtMoney(tx.remaining) : '—'}</ReadonlyValue>
          </Field>
          <Field id="pm-amount" label={t(tx?.kind === 'income' ? 'finance.pay.form.amountIn' : 'finance.pay.form.amountOut')} error={errors.amount?.message}>
            <Controller
              control={form.control}
              name="amount"
              render={({ field }) => <NumberInput id="pm-amount" value={field.value} onChange={field.onChange} onBlur={field.onBlur} minDecimalsOnBlur={2} suffix="₺" disabled={!partial} aria-invalid={!!errors.amount} />}
            />
          </Field>
          {tx ? (
            <div className="rounded-lg border bg-muted/20 p-3 text-xs sm:col-span-2">
              <p className="mb-1 font-medium text-foreground">{t(tx.kind === 'income' ? 'finance.pay.form.payer' : 'finance.pay.form.payee')}</p>
              <p className="text-sm">{partyName(tx.counterpartyId)}</p>
              <p className="text-muted-foreground">{party.get(tx.counterpartyId)?.bankName || t('finance.pay.form.noBank')}</p>
              {party.get(tx.counterpartyId)?.ibanMasked ? (
                <div className="mt-1.5">
                  <SensitiveIban target="party" id={tx.counterpartyId} masked={party.get(tx.counterpartyId)?.ibanMasked ?? ''} />
                </div>
              ) : null}
            </div>
          ) : null}
          <CheckRow {...form.register('partial', { onChange: (e: React.ChangeEvent<HTMLInputElement>) => !e.target.checked && tx && form.setValue('amount', tx.remaining) })} hint={t('finance.pay.form.partialHint')}>
            {t('finance.pay.form.partial')}
          </CheckRow>
        </FormSection>

        <FormSection title={t('finance.pay.form.sectionDetails')}>
          <Field id="pm-date" label={t('finance.pay.date')}>
            <Controller control={form.control} name="date" render={({ field }) => <DateField id="pm-date" value={field.value} onChange={field.onChange} clearable={false} />} />
          </Field>
          <Field id="pm-account" label={t('finance.pay.account')} error={errors.accountId?.message}>
            <FormSelect id="pm-account" control={form.control} name="accountId" placeholder={t('common.select')} options={accounts.filter((a) => a.active).map((a) => ({ value: a.id, label: a.name, description: [accountDetail(a.id), fmtMoney(a.balance)].filter(Boolean).join(' · ') }))} />
          </Field>
          <Field id="pm-channel" label={t('finance.pay.channel')}>
            <FormSelect id="pm-channel" control={form.control} name="channel" options={PAYMENT_CHANNELS.map((c) => ({ value: c, label: t(`finance.channel.${c}`) }))} />
          </Field>
          <Field id="pm-ref" label={t('finance.pay.reference')}>
            <Input id="pm-ref" {...form.register('reference')} />
          </Field>
          <Field id="pm-note" label={t('finance.pay.note')} full>
            <Input id="pm-note" maxLength={240} {...form.register('note')} />
          </Field>
          <div className="flex items-center gap-3 rounded-lg border border-dashed p-3 sm:col-span-2">
            <Paperclip className="size-5 shrink-0 text-muted-foreground" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{t('finance.pay.form.receipt')}</p>
              <p className="truncate text-xs text-muted-foreground">{fileName || t('finance.pay.form.receiptHint')}</p>
            </div>
            <input ref={fileRef} type="file" accept="application/pdf,image/*" className="sr-only" tabIndex={-1} aria-hidden onChange={(e) => setFileName(e.target.files?.[0]?.name ?? '')} />
            <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
              {t('finance.pay.form.pickFile')}
            </Button>
          </div>
        </FormSection>
      </div>
    </Modal>
  )
}
